import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Loader2, Search, CheckCircle2 } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { branchDisplay } from "../utils/workspace";

const ANY = "__any__";

/** Admin: απόδοση Καταστήματος σε υποθέσεις ΧΩΡΙΣ πινάκιο (απροσδιόριστες) ώστε να μετρούν στο σωστό κατάστημα. */
export const BranchAttributionCard = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ branch: "", gak_year: new Date().getFullYear().toString(), court_type: "", gak_from: "", gak_to: "", subject: "", only_unassigned: true });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/branch-attribution/status`).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const body = () => ({
    branch: form.branch, gak_year: form.gak_year || null, court_type: form.court_type || null, subject: form.subject || null,
    gak_from: form.gak_from === "" ? null : Number(form.gak_from), gak_to: form.gak_to === "" ? null : Number(form.gak_to), only_unassigned: form.only_unassigned,
  });
  const doPreview = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/admin/branch-attribution/preview`, body()); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα προεπισκόπησης"); }
    finally { setBusy(false); }
  };
  const doApply = async () => {
    if (!form.branch) { toast.error("Επιλέξτε Κατάστημα"); return; }
    if (!preview || !window.confirm(`Απόδοση ${preview.count} υποθέσεων στο «${branchDisplay(form.branch)}»;`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/branch-attribution/apply`, body());
      toast.success(`Αποδόθηκαν ${r.data.updated} υποθέσεις στο ${branchDisplay(r.data.branch)}`);
      setPreview(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα απόδοσης"); }
    finally { setBusy(false); }
  };

  const byBranch = {};
  (status?.rows || []).forEach((r) => { byBranch[r.branch] = (byBranch[r.branch] || 0) + r.count; });

  return (
    <Card data-testid="branch-attribution-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Building2 className="w-5 h-5 text-sky-700" />Απόδοση Καταστήματος σε Απροσδιόριστες Υποθέσεις</CardTitle>
        <CardDescription>
          Οι υποθέσεις χωρίς πινάκιο δεν μπορούν να αποδοθούν αυτόματα σε Κατάστημα και μετρούν στο κεντρικό. Εδώ τις αποδίδετε μαζικά
          (κατά έτος / τύπο / εύρος ΓΑΚ / αντικείμενο) ώστε Πίνακας Ελέγχου, Στατιστικά και CT Rate να βγαίνουν σωστά ανά Κατάστημα.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status && (
          <div className="text-sm flex flex-wrap gap-2" data-testid="branch-attribution-status">
            <span className="font-medium text-slate-700">Χωρίς πινάκιο: {status.total_unscheduled}</span>
            {Object.entries(byBranch).map(([b, n]) => (
              <span key={b} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">{b === "(χωρίς κατάστημα)" ? b : branchDisplay(b)}: {n}</span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Κατάστημα (στόχος)</Label>
            <Select value={form.branch || ANY} onValueChange={(v) => setForm({ ...form, branch: v === ANY ? "" : v })}>
              <SelectTrigger data-testid="attr-branch-select"><SelectValue placeholder="Επιλέξτε…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>— Επιλέξτε —</SelectItem>
                {(status?.branches || []).map((b) => <SelectItem key={b} value={b}>{branchDisplay(b)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Έτος ΓΑΚ</Label><Input value={form.gak_year} onChange={(e) => setForm({ ...form, gak_year: e.target.value })} data-testid="attr-year" /></div>
          <div>
            <Label className="text-xs">Τύπος</Label>
            <Select value={form.court_type || ANY} onValueChange={(v) => setForm({ ...form, court_type: v === ANY ? "" : v })}>
              <SelectTrigger data-testid="attr-court-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Όλοι</SelectItem><SelectItem value="Μονομελές">Μονομελές</SelectItem><SelectItem value="Πολυμελές">Πολυμελές</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">ΓΑΚ από</Label><Input type="number" value={form.gak_from} onChange={(e) => setForm({ ...form, gak_from: e.target.value })} data-testid="attr-gak-from" /></div>
          <div><Label className="text-xs">ΓΑΚ έως</Label><Input type="number" value={form.gak_to} onChange={(e) => setForm({ ...form, gak_to: e.target.value })} data-testid="attr-gak-to" /></div>
          <div className="col-span-2"><Label className="text-xs">Αντικείμενο (προαιρετικό)</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="π.χ. ΕΝΟΧΙΚΟ" data-testid="attr-subject" /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm mt-5">
            <input type="checkbox" checked={form.only_unassigned} onChange={(e) => setForm({ ...form, only_unassigned: e.target.checked })} data-testid="attr-only-unassigned" />
            Μόνο όσες δεν έχουν ήδη αποδοθεί σε περιφερειακή έδρα
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={doPreview} disabled={busy} data-testid="attr-preview-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}Προεπισκόπηση
          </Button>
          <Button size="sm" onClick={doApply} disabled={busy || !preview || !preview.count || !form.branch} data-testid="attr-apply-btn">
            <CheckCircle2 className="w-4 h-4 mr-1" />Απόδοση {preview?.count ? `(${preview.count})` : ""}
          </Button>
          {preview && <span className="text-sm text-slate-600" data-testid="attr-preview-count">Βρέθηκαν {preview.count} υποθέσεις χωρίς πινάκιο</span>}
        </div>
        {preview?.sample?.length > 0 && (
          <div className="text-xs text-slate-500 flex flex-wrap gap-1">
            {preview.sample.map((c) => <span key={c.gak} className="px-1.5 py-0.5 bg-slate-50 border rounded">{c.gak} · {c.court_type} · {c.subject || "—"}</span>)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
